/**
 * Shape catalogue for the QR renderer.
 *
 * Every shape emits SVG in *module units*: a body shape fills the box
 * (0,0)–(1,1), an eye frame the 7×7 finder box, an eye ball the 3×3 centre.
 * The renderer scales the whole grid, so shapes never deal with pixels.
 *
 * Body shapes receive their dark-neighbour map, which lets connected styles
 * (smooth, connected, leaf variants) react to what sits next to them.
 */

/** Trim float noise so the generated SVG stays small. */
function n(value) {
    return Number(value.toFixed(3));
}

function rect(x, y, w, h, rx) {
    return `<rect x="${n(x)}" y="${n(y)}" width="${n(w)}" height="${n(h)}"${rx ? ` rx="${n(rx)}"` : ''}/>`;
}

function circle(cx, cy, r) {
    return `<circle cx="${n(cx)}" cy="${n(cy)}" r="${n(r)}"/>`;
}

function path(d, extra = '') {
    return `<path d="${d}"${extra}/>`;
}

/** Rounded rectangle path with per-corner radii [tl, tr, br, bl]. */
function roundedPath(x, y, w, h, radii) {
    const [tl, tr, br, bl] = radii.map((r) => Math.max(0, Math.min(r, w / 2, h / 2)));
    return [
        `M${n(x + tl)},${n(y)}`,
        `H${n(x + w - tr)}`,
        tr ? `A${n(tr)},${n(tr)} 0 0 1 ${n(x + w)},${n(y + tr)}` : `V${n(y)}`,
        `V${n(y + h - br)}`,
        br ? `A${n(br)},${n(br)} 0 0 1 ${n(x + w - br)},${n(y + h)}` : `H${n(x + w)}`,
        `H${n(x + bl)}`,
        bl ? `A${n(bl)},${n(bl)} 0 0 1 ${n(x)},${n(y + h - bl)}` : `V${n(y + h)}`,
        `V${n(y + tl)}`,
        tl ? `A${n(tl)},${n(tl)} 0 0 1 ${n(x + tl)},${n(y)}` : `H${n(x)}`,
        'Z',
    ].join(' ');
}

function octagonPath(x, y, s, cut) {
    return [
        `M${n(x + cut)},${n(y)}`,
        `L${n(x + s - cut)},${n(y)}`,
        `L${n(x + s)},${n(y + cut)}`,
        `L${n(x + s)},${n(y + s - cut)}`,
        `L${n(x + s - cut)},${n(y + s)}`,
        `L${n(x + cut)},${n(y + s)}`,
        `L${n(x)},${n(y + s - cut)}`,
        `L${n(x)},${n(y + cut)}`,
        'Z',
    ].join(' ');
}

function diamondPath(x, y, s, inset = 0) {
    const c = x + s / 2;
    const m = y + s / 2;
    const half = s / 2 - inset;
    return `M${n(c)},${n(m - half)} L${n(c + half)},${n(m)} L${n(c)},${n(m + half)} L${n(c - half)},${n(m)} Z`;
}

/**
 * Square with quadratic-curved sides.
 * `bulge > 0` pushes the edges outward (pillow), `< 0` pulls them in (star).
 * Each edge midpoint ends up `bulge * s / 2` away from the straight line, so
 * callers must inset the box when bulging outward inside a fixed cell.
 */
function curvedSquarePath(x, y, s, bulge) {
    const cx = x + s / 2;
    const cy = y + s / 2;
    const d = bulge * s;
    return [
        `M${n(x)},${n(y)}`,
        `Q${n(cx)},${n(y - d)} ${n(x + s)},${n(y)}`,
        `Q${n(x + s + d)},${n(cy)} ${n(x + s)},${n(y + s)}`,
        `Q${n(cx)},${n(y + s + d)} ${n(x)},${n(y + s)}`,
        `Q${n(x - d)},${n(cy)} ${n(x)},${n(y)}`,
        'Z',
    ].join(' ');
}

/* ------------------------------------------------------------------ *
 * Body shapes                                                         *
 * ------------------------------------------------------------------ */

const body = {};

body.square = (x, y) => rect(x, y, 1, 1);

body.mosaic = (x, y) => rect(x + 0.06, y + 0.06, 0.88, 0.88);

body.dot = (x, y) => circle(x + 0.5, y + 0.5, 0.5);

body.circle = (x, y) => circle(x + 0.5, y + 0.5, 0.42);

body['circle-small'] = (x, y) => circle(x + 0.5, y + 0.5, 0.32);

body.rounded = (x, y) => rect(x, y, 1, 1, 0.25);

body['extra-rounded'] = (x, y) => rect(x, y, 1, 1, 0.42);

body.smooth = (x, y, nb) => path(roundedPath(x, y, 1, 1, [
    !nb.n && !nb.w ? 0.35 : 0,
    !nb.n && !nb.e ? 0.35 : 0,
    !nb.s && !nb.e ? 0.35 : 0,
    !nb.s && !nb.w ? 0.35 : 0,
]));

body['smooth-round'] = (x, y, nb) => path(roundedPath(x, y, 1, 1, [
    !nb.n && !nb.w ? 0.5 : 0,
    !nb.n && !nb.e ? 0.5 : 0,
    !nb.s && !nb.e ? 0.5 : 0,
    !nb.s && !nb.w ? 0.5 : 0,
]));

body.classy = (x, y) => path(roundedPath(x, y, 1, 1, [0.5, 0, 0.5, 0]));

body['classy-alt'] = (x, y) => path(roundedPath(x, y, 1, 1, [0, 0.5, 0, 0.5]));

body['classy-rounded'] = (x, y, nb) => path(roundedPath(x, y, 1, 1, [
    !nb.n && !nb.w ? 0.5 : 0.15,
    0.15,
    !nb.s && !nb.e ? 0.5 : 0.15,
    0.15,
]));

body.leaf = (x, y) => path(roundedPath(x, y, 1, 1, [0.5, 0.5, 0, 0.5]));

body.diamond = (x, y) => path(diamondPath(x, y, 1));

body['diamond-small'] = (x, y) => path(diamondPath(x, y, 1, 0.12));

body.pointed = (x, y) => path([
    `M${n(x + 0.5)},${n(y)}`,
    `Q${n(x + 1)},${n(y)} ${n(x + 1)},${n(y + 0.5)}`,
    `Q${n(x + 1)},${n(y + 1)} ${n(x + 0.5)},${n(y + 1)}`,
    `Q${n(x)},${n(y + 1)} ${n(x)},${n(y + 0.5)}`,
    `Q${n(x)},${n(y)} ${n(x + 0.5)},${n(y)}`,
    'Z',
].join(' '));

body.star = (x, y) => path(curvedSquarePath(x, y, 1, -0.5));

body.pillow = (x, y) => path(curvedSquarePath(x + 0.1, y + 0.1, 0.8, 0.25));

body['edge-cut'] = (x, y) => path(octagonPath(x, y, 1, 0.22));

body['edge-cut-deep'] = (x, y) => path(octagonPath(x, y, 1, 0.36));

body.cross = (x, y) => `${rect(x + 0.3, y, 0.4, 1)}${rect(x, y + 0.3, 1, 0.4)}`;

body['zebra-row'] = (x, y) => (y % 2 === 0 ? circle(x + 0.5, y + 0.5, 0.5) : rect(x, y, 1, 1));

body['zebra-col'] = (x, y) => (x % 2 === 0 ? circle(x + 0.5, y + 0.5, 0.5) : rect(x, y, 1, 1));

body.connected = (x, y, nb) => {
    const parts = [circle(x + 0.5, y + 0.5, 0.5)];
    if (nb.e) parts.push(rect(x + 0.5, y + 0.15, 0.5, 0.7));
    if (nb.s) parts.push(rect(x + 0.15, y + 0.5, 0.7, 0.5));
    return parts.join('');
};

body.stripe = (x, y) => path([
    `M${n(x + 0.25)},${n(y)}`,
    `L${n(x + 1)},${n(y)}`,
    `L${n(x + 0.75)},${n(y + 1)}`,
    `L${n(x)},${n(y + 1)}`,
    'Z',
].join(' '));

export const BODY_SHAPES = Object.keys(body);
export const bodyShapeRenderer = (id) => body[id] || body.square;

/* ------------------------------------------------------------------ *
 * Eye frames — the 7×7 ring around each finder pattern                *
 * ------------------------------------------------------------------ */

/** Ring drawn as a 1-module stroke, so the finder keeps its exact geometry. */
function strokedRect(rx, width = 1, inset = 0) {
    const half = width / 2;
    const x = half + inset;
    const size = 7 - width - inset * 2;
    return rect(x, x, size, size, rx)
        .replace('/>', ` fill="none" stroke-width="${n(width)}"/>`);
}

const eyeFrame = {};

eyeFrame.square = () => strokedRect(0);

eyeFrame.rounded = () => strokedRect(1.6);

eyeFrame['extra-rounded'] = () => strokedRect(2.6);

eyeFrame.circle = () => circle(3.5, 3.5, 3).replace('/>', ' fill="none" stroke-width="1"/>');

eyeFrame.thin = () => strokedRect(0, 0.6);

eyeFrame['thin-rounded'] = () => strokedRect(1.8, 0.6);

/** Ring following an arbitrary rounded-corner profile. */
function strokedCorners(radii) {
    return path(roundedPath(0.5, 0.5, 6, 6, radii), ' fill="none" stroke-width="1"');
}

eyeFrame.leaf = () => strokedCorners([3, 0, 3, 0]);

eyeFrame['leaf-alt'] = () => strokedCorners([0, 3, 0, 3]);

eyeFrame['corner-tl'] = () => strokedCorners([2.6, 0, 0, 0]);

eyeFrame['corner-br'] = () => strokedCorners([0, 0, 2.6, 0]);

eyeFrame.cut = () => path(octagonPath(0.5, 0.5, 6, 1.7), ' fill="none" stroke-width="1"');

eyeFrame.dotted = () => {
    const parts = [];
    for (let i = 0; i < 7; i += 1) {
        for (let j = 0; j < 7; j += 1) {
            const onRing = i === 0 || j === 0 || i === 6 || j === 6;
            if (onRing) parts.push(circle(i + 0.5, j + 0.5, 0.45));
        }
    }
    return parts.join('');
};

eyeFrame.pixel = () => {
    const parts = [];
    for (let i = 0; i < 7; i += 1) {
        for (let j = 0; j < 7; j += 1) {
            const onRing = i === 0 || j === 0 || i === 6 || j === 6;
            if (onRing) parts.push(rect(i + 0.08, j + 0.08, 0.84, 0.84));
        }
    }
    return parts.join('');
};

// Gaps stay small so the finder still reads as a solid 1:1:3:1:1 ring.
eyeFrame.dashed = () => strokedRect(0).replace('/>', ' stroke-dasharray="2.6 0.35"/>');

// Both strokes sit inside the one-module ring band, so the finder geometry is
// unchanged — a purely decorative split.
eyeFrame.double = () => `${strokedRect(0, 0.42, -0.29)}${strokedRect(0, 0.42, 0.29)}`;

export const EYE_FRAME_SHAPES = Object.keys(eyeFrame);
export const eyeFrameRenderer = (id) => eyeFrame[id] || eyeFrame.square;

/* ------------------------------------------------------------------ *
 * Eye balls — the 3×3 centre of each finder pattern                   *
 * ------------------------------------------------------------------ */

const eyeBall = {};

eyeBall.square = () => rect(0, 0, 3, 3);

eyeBall.rounded = () => rect(0, 0, 3, 3, 0.8);

eyeBall['extra-rounded'] = () => rect(0, 0, 3, 3, 1.2);

eyeBall.circle = () => circle(1.5, 1.5, 1.5);

eyeBall.leaf = () => path(roundedPath(0, 0, 3, 3, [1.5, 0, 1.5, 0]));

eyeBall['leaf-alt'] = () => path(roundedPath(0, 0, 3, 3, [0, 1.5, 0, 1.5]));

eyeBall['corner-tl'] = () => path(roundedPath(0, 0, 3, 3, [1.5, 0, 0, 0]));

eyeBall['corner-br'] = () => path(roundedPath(0, 0, 3, 3, [0, 0, 1.5, 0]));

eyeBall.diamond = () => path(diamondPath(0, 0, 3));

eyeBall.star = () => path(curvedSquarePath(0, 0, 3, -0.22));

eyeBall.pillow = () => path(curvedSquarePath(0.2, 0.2, 2.6, 0.15));

eyeBall.cut = () => path(octagonPath(0, 0, 3, 0.85));

eyeBall.cross = () => `${rect(0.9, 0, 1.2, 3)}${rect(0, 0.9, 3, 1.2)}`;

eyeBall.dots = () => {
    const parts = [];
    for (let i = 0; i < 3; i += 1) {
        for (let j = 0; j < 3; j += 1) parts.push(circle(i + 0.5, j + 0.5, 0.52));
    }
    return parts.join('');
};

eyeBall.pixel = () => {
    const parts = [];
    for (let i = 0; i < 3; i += 1) {
        for (let j = 0; j < 3; j += 1) parts.push(rect(i + 0.03, j + 0.03, 0.94, 0.94));
    }
    return parts.join('');
};

eyeBall['bars-v'] = () => [0, 1, 2].map((i) => rect(i + 0.04, 0, 0.92, 3, 0.3)).join('');

eyeBall['bars-h'] = () => [0, 1, 2].map((i) => rect(0, i + 0.04, 3, 0.92, 0.3)).join('');

eyeBall.stripe = () => path([
    `M${n(0.75)},0`,
    'L3,0',
    `L${n(2.25)},3`,
    'L0,3',
    'Z',
].join(' '));

export const EYE_BALL_SHAPES = Object.keys(eyeBall);
export const eyeBallRenderer = (id) => eyeBall[id] || eyeBall.square;
